import * as THREE from "three";
import type { SlotName } from "./types";
import { hideGroupOf, towerOfModel } from "./hide";

/**
 * Material slots of the stock tower models. The towers are one painted atlas (`<model>_tex`) and its glow twin, so a
 * slot is not a glTF material: it is the PART a vertex belongs to (`<model>.drum`, `.roof`, `.deck`, …; art/hq/README.md).
 * After RigidRig.merge every vertex names its part through its bone, and `tag` stores the slot per vertex in a
 * one-byte `tdSlot` attribute on the shared template geometry (every clone and every LOD copy carries it), which the
 * kit's body material (kit/bodyMaterial.ts) reads to give each slot its own look.
 */

/** Slot index per name, as stored in `tdSlot`. ELEMENT is never restyled (readability: the element glow says what a tower is). */
export const SLOT_INDEX: Readonly<Record<SlotName | "element", number>> = { stone: 0, wood: 1, roof: 2, metal: 3, cloth: 4, element: 5 };
export const SLOT_COUNT = 6;

/**
 * Part name (digits stripped) → slot. Anything unlisted is stone. The weapon's sub-parts are metal; the element parts
 * (crystals, coils, flames, the brew, the beacon's orb and halo, the frost tower's ice and snow, the glowing rune ring,
 * the tier-3 gems) keep their paint.
 */
export const SLOT_OF_PART: Readonly<Record<string, SlotName | "element">> = {
  drum: "stone", plinth: "stone", body: "stone",
  deck: "wood", scaffold: "wood", door: "wood",
  roof: "roof",
  barrel: "metal", ring: "metal",
  weapon: "metal", lamp: "metal", bow_l: "metal", bow_r: "metal", arrow: "metal", string: "metal", bolt: "metal", bore: "metal", nozzle: "metal", gun_tip: "metal", nozzle_tip: "metal",
  flag: "cloth",
  core: "element", ice: "element", snow: "element", flame: "element", coil: "element", crystal: "element", brew: "element", bubble: "element",
  halo: "element", orb: "element", shard: "element", gem: "element", rune: "element",
  // Towers v2: the mortar's smoke puff; the hex totem's mask with its glowing eyes (its face: what says "hex" under
  // any skin), its ward circle and its charms keep their paint
  smoke: "element", mask: "element", eye: "element", ward: "element", charm: "element",
  // the tier-IV branches' parts (census of 2026-10-21): the gatling's barrels, the rocket battery and the war drums
  // restyle; the storm and plague clouds, the blizzard's swirl, the glacier's spires, the disintegrator's lens, the
  // phoenix (wings and tail), the venom spider and the spirit wisps are each branch's element
  spin: "metal", rocket: "metal", wardrum: "wood",
  cloud: "element", swirl: "element", spire: "element", lens: "element", wing: "element", tail: "element", spider: "element", wisp: "element",
  // glow-only parts (their meshes are `_glow` and never restyled anyway): named here so no part reads as stone
  spark: "element", prism: "element", beam: "element", egg: "element", glow: "element",
};

/** Per tower type: parts whose slot differs there (frost's tier-IV crystal "roof" is its element: never restyled). */
export const SLOT_OVERRIDES: Readonly<Record<string, Readonly<Record<string, SlotName | "element">>>> = {
  frost: { roof: "element" },
};

/**
 * A part name as the tables know it: as named, else without digits (`bow1_l` → `bow_l`, `flame20` → `flame`), else
 * without a left/right suffix too (`door_l` → `door`, `wing_r` → `wing`). Null stays null.
 */
export function canonicalPart(part: string | null): string | null {
  if (part === null || part in SLOT_OF_PART) return part;
  const noDigits = part.replace(/\d+/g, "");
  if (noDigits in SLOT_OF_PART) return noDigits;
  const bare = noDigits.replace(/_[lr]$/, "");
  return bare in SLOT_OF_PART ? bare : noDigits;
}

/** The part a node belongs to under model `model`: its own or its nearest named ancestor's (`<model>.<part><n>`). */
export function partOf(node: THREE.Object3D | null, model: string): string | null {
  for (let o = node; o; o = o.parent) {
    const n = o.name ?? "";
    // GLTFLoader drops the dot (`tower_archer_t1drum`), so accept both spellings (views/common.ts hqNode)
    if (n.startsWith(`${model}.`)) return n.slice(model.length + 1).replace(/\d+$/, "");
    if (n.startsWith(model) && n.length > model.length) return n.slice(model.length).replace(/\d+$/, "");
  }
  return null;
}

/** The slot index of a part name (on tower type `tower`, whose overrides win). */
export function slotOfPart(part: string | null, tower: string | null = null): number {
  const p = canonicalPart(part);
  const over = p && tower ? SLOT_OVERRIDES[tower]?.[p] : undefined;
  return SLOT_INDEX[over ?? (p ? SLOT_OF_PART[p] : undefined) ?? "stone"];
}

export class SkinSlots {
  /** Models whose geometry carries `tdSlot`: the towers (the fortress skins are whole models, troops their own GLB). */
  static readonly Families = /^tower_/;

  /**
   * Tags every skinned (rigid-rig) mesh under a loaded model's template with its vertices' slots (`tdSlot`) and the
   * hide group each may be hidden by (`tdHide`, kit/hide.ts; 0 = never). Idempotent.
   */
  static tag(root: THREE.Object3D, model: string): void {
    if (!SkinSlots.Families.test(model)) return;
    const tower = towerOfModel(model);
    root.traverse((o) => {
      const skin = o as THREE.SkinnedMesh;
      if (!skin.isSkinnedMesh || skin.geometry.getAttribute("tdSlot")) return;
      const index = skin.geometry.getAttribute("skinIndex");
      if (!index) return;
      const parts = skin.skeleton.bones.map((b) => canonicalPart(partOf(b, model)));
      const bySlot = parts.map((p) => slotOfPart(p, tower));
      // the element slot is never hidden, whatever its part is called (readability)
      const byHide = parts.map((p, i) => (bySlot[i] === SLOT_INDEX.element ? 0 : hideGroupOf(tower, p)));
      const slots = new Uint8Array(index.count), hide = new Uint8Array(index.count);
      for (let i = 0; i < index.count; i++) { const b = index.getX(i); slots[i] = bySlot[b] ?? 0; hide[i] = byHide[b] ?? 0; }
      skin.geometry.setAttribute("tdSlot", new THREE.BufferAttribute(slots, 1));
      skin.geometry.setAttribute("tdHide", new THREE.BufferAttribute(hide, 1));
    });
  }
}
